import React from 'react'
import classNames from 'classnames'
import { Case, Default, Else, If, Switch, Then, When } from 'react-if'
import type { Stripe } from 'stripe'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { AsP, AsSpan, CaptionSM, TitleMD } from '@/atoms/Text'
import { GUILD_BASIC_YEARLY_FULL_PRICE, GUILD_PREMIUM_YEARLY_FULL_PRICE } from '@/constants/guild'
import { formatPrice } from '@/services/PriceFormatter'
import { formatPromotionPriceDisplay, getPromotionalPrice, ValidatedPromotion } from '@/services/SaleService'
import { GuildPlanSelectionNew } from '@/utils/GuildUtil/GuildUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useTimeLeft } from '@/utils/useTimeLeft/useTimeLeft'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

interface Props extends GuildPlanSelectionNew {
  className?: string
  offerId: string
  planInterval: 'annual' | 'monthly'
  disclosePriceInUSD?: boolean
  separateTierPlan?: boolean
}

export const GuildPricingLabel: React.FC<Props> = ({
  className,
  currency = 'USD',
  price,
  monthlyPrice,
  tier,
  planInterval,
  offerId,
}) => {
  return (
    <div className={className}>
      <PricePerInterval
        offerId={offerId}
        planInterval={planInterval}
        price={price}
        monthlyPrice={monthlyPrice}
        tier={tier}
        currency={currency}
      />
      <div className="mt-1">
        <PriceDescriptor
          price={price}
          monthlyPrice={monthlyPrice}
          currency={currency}
          offerId={offerId}
          planInterval={planInterval}
          tier={tier}
        />
      </div>
    </div>
  )
}

type PriceDescriptorProps = Pick<Props, 'tier' | 'planInterval' | 'offerId' | 'price' | 'currency' | 'monthlyPrice'>

export const PriceDescriptor: React.FC<PriceDescriptorProps> = ({
  price,
  currency = 'USD',
  tier,
  planInterval,
  offerId,
  monthlyPrice,
}) => {
  const { locale } = useLocale()
  const { t } = useTranslate('guild')
  const { sale } = useGuildSignupContext()
  const promotion = sale?.promotions.find((p) => p.offer.id === offerId) as ValidatedPromotion
  const isPromoApplicable = Boolean(promotion)

  const promotionMonths = promotion?.promotionCode?.coupon?.duration_in_months
  const promotionalPrice = getPromotionalPrice(promotion)
  const thatsLessThanPricePerMonth = formatPrice(Math.ceil((promotionalPrice as number) / 12), {
    locale,
    currency,
    includeDecimals: false,
  })

  const yearlyPricePerMonth = isPromoApplicable
    ? thatsLessThanPricePerMonth
    : formatPrice(Math.ceil((price as number) / 12), {
        locale,
        currency,
        includeDecimals: false,
      })

  let savings = 0

  if (isPromoApplicable && promotionalPrice) {
    if (planInterval === 'monthly') {
      const multiplier = getPromotionMonths(promotion.promotionCode)
      savings = (monthlyPrice - promotionalPrice) * multiplier
    } else {
      let fullPrice = GUILD_BASIC_YEARLY_FULL_PRICE
      if (tier === 'premium') {
        fullPrice = GUILD_PREMIUM_YEARLY_FULL_PRICE
      } else if (tier === 'basic-no-ads') {
        fullPrice = monthlyPrice * 12
      }
      savings = fullPrice - promotionalPrice
    }
  }

  const formattedSavings = formatPrice(savings, {
    locale,
    currency,
    includeDecimals: false,
  })

  const shouldShowSavingsText = Boolean(savings)

  return (
    <If condition={isPromoApplicable}>
      <Then>
        <Switch>
          <Case condition={shouldShowSavingsText}>
            <CaptionSM color="core-gray-700" className="mb-3 max-w-[140px] text-balance">
              <If condition={planInterval === 'monthly'}>
                <Then>
                  <Translate t={t} i18nKey="totalSavingsForMonthly" values={{ formattedSavings, promotionMonths }}>
                    (That&apos;s <span className="font-bold">{{ formattedSavings }}</span> savings over your first{' '}
                    {{ promotionMonths }} months)
                  </Translate>
                </Then>
                <Else>
                  <Translate t={t} i18nKey="yearlySavings" values={{ formattedSavings }}>
                    (That&apos;s <span className="font-bold">{{ formattedSavings }}</span> savings)
                  </Translate>
                </Else>
              </If>
            </CaptionSM>
          </Case>
          <Case condition={isPromotionOneTime(promotion?.promotionCode)}>
            <CaptionSM color="core-gray-700" className="mb-3 max-w-[140px] text-balance">
              <If condition={planInterval === 'monthly'}>
                <Then>
                  <Translate t={t} i18nKey="firstMonthSavingsBilledMonthlyV2">
                    For the first month, billed monthly
                  </Translate>
                </Then>
                <Else>
                  <Translate t={t} i18nKey="thatsLikePricePerMonthFirstYear" values={{ yearlyPricePerMonth }}>
                    (That&apos;s like {{ yearlyPricePerMonth }}/month for the first year)
                  </Translate>
                </Else>
              </If>
            </CaptionSM>
          </Case>
          <Case condition={isPromotionNTimes(promotion?.promotionCode) && planInterval === 'monthly'}>
            <CaptionSM color="core-gray-700" className="mb-3 max-w-[140px] text-balance">
              <Translate t={t} i18nKey="firstNMonthsSavingsBilledMonthlyV2" values={{ promotionMonths }}>
                For the first {{ promotionMonths }} months, billed monthly
              </Translate>
            </CaptionSM>
          </Case>
          <Case condition={sale?.endDate}>
            <CountdownToSave endDate={new Date(sale?.endDate as string)} isAlreadyReduced={planInterval === 'annual'} />
          </Case>
        </Switch>
      </Then>
      <Else>
        <CaptionSM color="core-gray-700" className="mb-3">
          <Switch>
            <Case condition={tier === 'premium' && planInterval === 'annual' && sale?.abbreviatedPitch}>
              {sale?.abbreviatedPitch}
            </Case>
            <Case condition={planInterval === 'annual'}>
              <Translate t={t} i18nKey="thatsLikePricePerMonthV1" values={{ yearlyPricePerMonth }}>
                (That&apos;s like {{ yearlyPricePerMonth }}/month)
              </Translate>
            </Case>
            <Case condition={tier === 'basic' && planInterval === 'monthly'}>
              {t('billedMonthly', 'Billed monthly')}
            </Case>
            <Case condition={tier === 'basic-no-ads' && planInterval === 'monthly'}>
              {t('billedMonthly', 'Billed monthly')}
            </Case>
            <Case condition={tier === 'premium' && planInterval === 'monthly'}>
              {t('billedMonthly', 'Billed monthly')}
            </Case>
          </Switch>
        </CaptionSM>
      </Else>
    </If>
  )
}

export const PricePerInterval: React.FC<Props> = ({
  className,
  currency = 'USD',
  price,
  monthlyPrice,
  tier,
  planInterval,
  offerId,
  disclosePriceInUSD,
  separateTierPlan = false,
}) => {
  const { locale } = useLocale()
  const { t } = useTranslate('guild')
  const { sale } = useGuildSignupContext()
  const promotion = sale?.promotions.find((p) => p.offer.id === offerId) as ValidatedPromotion
  const isPromoApplicable = Boolean(promotion)
  const planPrice = planInterval === 'annual' ? price : monthlyPrice

  return (
    <TitleMD as={AsP} weight="bold" className={className}>
      <Switch>
        <Case condition={isPromoApplicable}>
          {!!promotion && (
            <Switch>
              <Case condition={planInterval === 'annual' && tier === 'premium' && !separateTierPlan}>
                <ExtraSavingsLabel
                  fullPrice={GUILD_PREMIUM_YEARLY_FULL_PRICE}
                  normalPrice={planPrice}
                  promoPrice={formatPromotionPriceDisplay(promotion, locale)}
                />
              </Case>
              <Case condition={planInterval === 'annual' && tier === 'basic' && !separateTierPlan}>
                <ExtraSavingsLabel
                  fullPrice={GUILD_BASIC_YEARLY_FULL_PRICE}
                  normalPrice={planPrice}
                  promoPrice={formatPromotionPriceDisplay(promotion, locale)}
                />
              </Case>
              <Case condition={planInterval === 'annual' && tier === 'basic-no-ads' && !separateTierPlan}>
                <ExtraSavingsLabel
                  fullPrice={monthlyPrice * 12}
                  normalPrice={planPrice}
                  promoPrice={formatPromotionPriceDisplay(promotion, locale)}
                />
              </Case>
              <Default>
                <span
                  className={classNames('text-core-gray-600 line-through decoration-warm-01 decoration-2', {
                    'lg:block lg:photon-title-lg lg:-mb-[6px] text-core-gray-950 !font-medium': separateTierPlan,
                  })}
                >
                  {formatPrice(planPrice, { locale, includeDecimals: false, currency: 'USD' })}
                </span>
                &nbsp;
                <span className="text-black">{formatPromotionPriceDisplay(promotion, locale)}</span>
              </Default>
            </Switch>
          )}
        </Case>
        <Case condition={planInterval === 'annual'}>
          <>
            <If condition={Boolean(promotion && tier === 'premium')}>
              <Then>
                <span
                  className={classNames('text-core-gray-600 line-through decoration-warm-01 decoration-2', {
                    'lg:block lg:photon-title-lg lg:-mb-[6px] text-core-gray-950 !font-medium': separateTierPlan,
                  })}
                >
                  {formatPrice(GUILD_PREMIUM_YEARLY_FULL_PRICE, { locale, includeDecimals: false, currency: 'USD' })}
                </span>
                &nbsp;
                <span className="text-black">{!!promotion && formatPromotionPriceDisplay(promotion, locale)}</span>
              </Then>
              <Else>
                <When condition={tier === 'premium'}>
                  <>
                    <span
                      className={classNames('text-core-gray-600 line-through decoration-warm-01 decoration-2', {
                        'lg:block lg:photon-title-lg lg:-mb-[6px] text-core-gray-950 !font-medium': separateTierPlan,
                      })}
                    >
                      {formatPrice(GUILD_PREMIUM_YEARLY_FULL_PRICE, {
                        locale,
                        includeDecimals: false,
                        currency: 'USD',
                      })}
                    </span>
                    &nbsp;
                    <span className="text-black">
                      {formatPrice(price, { locale, includeDecimals: false, currency })}
                    </span>
                  </>
                </When>
                <When condition={tier === 'basic'}>
                  <>
                    <span
                      className={classNames('text-core-gray-600 line-through decoration-warm-01 decoration-2', {
                        'lg:block lg:photon-title-lg lg:-mb-[6px] text-core-gray-950 !font-medium': separateTierPlan,
                      })}
                    >
                      {formatPrice(monthlyPrice * 12, { locale, includeDecimals: false, currency: 'USD' })}
                    </span>
                    &nbsp;
                    <span className="text-black">
                      {formatPrice(price, { locale, includeDecimals: false, currency })}
                    </span>
                  </>
                </When>
                <When condition={tier === 'basic-no-ads'}>
                  <>
                    <span
                      className={classNames('text-core-gray-600 line-through decoration-warm-01 decoration-2', {
                        'lg:block lg:photon-title-lg lg:-mb-[6px] text-core-gray-950 !font-medium': separateTierPlan,
                      })}
                    >
                      {formatPrice(monthlyPrice * 12, { locale, includeDecimals: false, currency: 'USD' })}
                    </span>
                    &nbsp;
                    <span className="text-black">
                      {formatPrice(price, { locale, includeDecimals: false, currency })}
                    </span>
                  </>
                </When>
              </Else>
            </If>
          </>
        </Case>
        <Default>
          <>{formatPrice(monthlyPrice, { locale, includeDecimals: false, currency })}</>
        </Default>
      </Switch>
      <span
        className={classNames('text-sm font-semibold lowercase text-core-gray-700', {
          'text-xl text-core-gray-950 !font-medium lg:font-semibold': separateTierPlan,
        })}
      >
        <When condition={separateTierPlan}>
          /{planInterval === 'annual' ? t('yearShort', 'yr') : t('monthShort', 'mo')}
        </When>
        <When condition={!separateTierPlan}>
          /{planInterval === 'annual' ? t('year', 'Year') : t('month', 'Month')}
        </When>
      </span>
      <When condition={disclosePriceInUSD}>
        <CaptionSM as={AsSpan} className="inline-block">
          *
        </CaptionSM>
      </When>
    </TitleMD>
  )
}

interface CountdownToSaveProps {
  endDate: Date
  isAlreadyReduced?: boolean
}
const CountdownToSave: React.FC<CountdownToSaveProps> = ({ endDate, isAlreadyReduced }) => {
  const { t } = useTranslate('join-the-guild')
  const { days, hours, minutes } = useTimeLeft({ targetDate: endDate })
  let display

  if (isAlreadyReduced) {
    if (days > 0) {
      display = t('daysToSaveAlreadyReduced', {
        days,
        count: days,
        defaultValue: '{{days}} day left to save on already reduced price!',
        defaultValue_other: '{{days}} days left to save on already reduced price!',
      })
    } else if (hours > 0) {
      display = t('hoursToSaveAlreadyReduced', {
        hours,
        count: hours,
        defaultValue: '{{hours}} hour left to save on already reduced price!',
        defaultValue_other: '{{hours}} hours left to save on already reduced price!',
      })
    } else if (minutes > 0) {
      display = t('minutesToSaveAlreadyReduced', {
        minutes,
        count: minutes,
        defaultValue: '{{minutes}} minute left to save on already reduced price!',
        defaultValue_other: '{{minutes}} minutes left to save on already reduced price!',
      })
    } else {
      display = t('lastChanceToSaveAlreadyReduced', 'Last chance to save on already reduced price!')
    }
  } else {
    if (days > 0) {
      display = t('daysToSave', {
        days,
        count: days,
        defaultValue: '{{days}} day left to save',
        defaultValue_other: '{{days}} days left to save',
      })
    } else if (hours > 0) {
      display = t('hoursToSave', {
        hours,
        count: hours,
        defaultValue: '{{hours}} hour left to save',
        defaultValue_other: '{{hours}} hours left to save',
      })
    } else if (minutes > 0) {
      display = t('minutesToSave', {
        minutes,
        count: minutes,
        defaultValue: '{{minutes}} minute left to save',
        defaultValue_other: '{{minutes}} minutes left to save',
      })
    } else {
      display = t('lastChanceToSave', 'Last chance to save!')
    }
  }

  return (
    <CaptionSM color="warm-01" className="mb-3" weight="bold">
      {display}
    </CaptionSM>
  )
}

function ExtraSavingsLabel({
  fullPrice,
  normalPrice,
  promoPrice,
}: {
  fullPrice: number
  normalPrice: number
  promoPrice: string | null
}) {
  const { locale } = useLocale()
  return (
    <>
      <span className="relative text-core-gray-600">
        <CloseIcon
          size={40}
          color="warm-01"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[12deg]"
        />
        {formatPrice(fullPrice, { locale, includeDecimals: false, currency: 'USD' })}
      </span>
      &nbsp;
      <span className="relative text-core-gray-600">
        <CloseIcon
          size={40}
          color="warm-01"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[12deg]"
        />
        {formatPrice(normalPrice, { locale, includeDecimals: false, currency: 'USD' })}
      </span>
      <br />
      <span className="inline-block text-black">{promoPrice}</span>
    </>
  )
}

function isPromotionOneTime(promotion: Stripe.PromotionCode) {
  if (!promotion) return false
  return (
    promotion.coupon.duration === 'once' ||
    (promotion.coupon.duration === 'repeating' && promotion.coupon.duration_in_months === 1)
  )
}

function isPromotionNTimes(promotion: Stripe.PromotionCode) {
  if (!promotion) return false
  return (
    promotion.coupon.duration === 'repeating' &&
    promotion.coupon.duration_in_months &&
    promotion.coupon.duration_in_months > 1
  )
}

function getPromotionMonths(promotion: Stripe.PromotionCode): number {
  if (!promotion) return 0

  return isPromotionNTimes(promotion) ? (promotion?.coupon?.duration_in_months as number) : 1
}
